/**
 * 32. Exchange Rates — currency grid; manual entry or import; run
 * revaluation.
 *
 * The From and To filters offer the currencies the rates are in, and filter
 * them. To starts at the workspace currency, the one rates are mostly into;
 * "All currencies" shows every rate (CUR-2: they used to be two inputs with
 * code placeholders that filtered nothing).
 *
 * Arabic and English (C7): every word from the catalogs.
 */
import { useState } from "react";
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  Toolbar,
  Field,
  Select,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useWorkspaceCurrency } from "@/lib/currency";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatDate } from "@/lib/money";

interface ExchangeRateRow {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  rate: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  source: string | null;
  isLocked: boolean;
}

export function ExchangeRatesPage() {
  const { data, isLoading } = useApiList<ExchangeRateRow>(
    ["finance", "exchange-rates"],
    "/finance/exchange-rates",
  );
  const { t } = useTranslation();
  const workspaceCurrency = useWorkspaceCurrency();
  const [from, setFrom] = useState("");
  // null until the person chooses: then the workspace currency applies.
  const [chosenTo, setChosenTo] = useState<string | null>(null);
  const to = chosenTo ?? workspaceCurrency ?? "";
  const rows = data?.items ?? [];
  const codes = (pick: (r: ExchangeRateRow) => string, keep?: string) =>
    [...new Set([...rows.map(pick), ...(keep ? [keep] : [])])].sort();
  const fromCodes = codes((r) => r.fromCurrency);
  const toCodes = codes((r) => r.toCurrency, to);
  const shown = rows.filter(
    (r) => (!from || r.fromCurrency === from) && (!to || r.toCurrency === to),
  );

  return (
    <div>
      <PageHeader
        title={t("finance.rates.title")}
        subtitle={t("finance.rates.subtitle")}
        actions={
          <PendingButton variant="primary" reason="revaluation" slice="AE-W7">
            {t("finance.rates.revalue")}
          </PendingButton>
        }
      />
      <Toolbar>
        <Field label={t("money.fromCurrency")}>
          <Select value={from} onChange={(e) => setFrom(e.target.value)}>
            <option value="">{t("money.allCurrencies")}</option>
            {fromCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("money.toCurrency")}>
          <Select value={to} onChange={(e) => setChosenTo(e.target.value)}>
            <option value="">{t("money.allCurrencies")}</option>
            {toCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </Select>
        </Field>
      </Toolbar>
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("finance.rates.list"), shown.length)}>
          <DataTable<ExchangeRateRow>
            rows={shown}
            columns={[
              { key: "fromCurrency", label: t("fields.from") },
              { key: "toCurrency", label: t("fields.to") },
              {
                key: "rate",
                label: t("finance.rates.rate"),
                // The decimal exactly as the API sent it (Latin digits in
                // every language), never through Number().
                render: (r) => <span className="atlas-mono">{r.rate}</span>,
              },
              {
                key: "effectiveFrom",
                label: t("finance.rates.effectiveFrom"),
                render: (r) => formatDate(r.effectiveFrom),
              },
              {
                key: "effectiveTo",
                label: t("finance.rates.effectiveTo"),
                render: (r) =>
                  r.effectiveTo ? formatDate(r.effectiveTo) : "—",
              },
              { key: "source", label: t("fields.source") },
              {
                key: "isLocked",
                label: t("finance.rates.locked"),
                render: (r) =>
                  r.isLocked ? t("finance.rates.lockedYes") : t("shared.no"),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
